import '@sentry/wrapper';
import './lit-dialog';

import { css, html, LitElement, nothing, PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { locales } from './locales';

interface DialogElement extends HTMLElement {
  openDialog: () => void;
  closeDialog: () => void;
}
declare global {
  interface Window {
    adobeid: any;
    adobeIMS: any;
    SENTRY_EVENTS: any[];
  }
}

// Holds navigation briefly so host-side work queued by the same click — the
// analytics events the functional suite reads off window.SENTRY_EVENTS — lands
// before location.assign() tears the document down.
function preRedirectWork() {
  return new Promise(resolve => {
    setTimeout(resolve, 500);
  });
}

const searchParams = new URLSearchParams(window.location.search);

// Redirect-mode social sign-up return signal (spec § Transport: popup vs
// redirect — contextual-social-signup.md). The IMS/Authz callback wrote the
// Federated JWT as an HttpOnly cookie on `.services.adobe.com` and redirected
// the user back to us with `?open_susi_light=true`. Detect the param,
// scrub it from the URL via `history.replaceState` (so a refresh / share
// doesn't re-trigger), and pass it to <susi-sentry-light> as a property —
// the wrapper forwards it to the iframe via `iframeState.resumeSocial`, and
// the iframe's `app.ts` hands off to the socialFederated flow at boot. The
// credential itself stays in the cookie, never in JavaScript.
const OPEN_SUSI_LIGHT_PARAM = 'open_susi_light';
const resumeSocialFromQuery =
  searchParams.get(OPEN_SUSI_LIGHT_PARAM) === 'true';

const authParams = [...searchParams.entries()].filter(
  ([key]) => key !== 'config' && key !== OPEN_SUSI_LIGHT_PARAM
);
const config = JSON.parse(searchParams.get('config') ?? '{}');
// `open_susi_light=true` in the return URL is honored purely on the
// presence of the redirect param — exactly what a production host does.
// Inline social sign-up enablement is NOT a host concern: it is server-driven
// per-client via `GET /v2/configurations` inside the iframe (see
// config/fetchConfig.ts and the flow's token-exchange-failed gate). If the
// feature is off for the client, the iframe escapes to Big SUSI on its own.
const resumeSocial = resumeSocialFromQuery;

// Strip the resume hint so a refresh / shared link doesn't re-trigger the
// bootstrap (spec § Host responsibilities in redirect mode, step 3).
if (resumeSocial) {
  const scrubbed = new URLSearchParams(window.location.search);
  scrubbed.delete(OPEN_SUSI_LIGHT_PARAM);
  const newSearch = scrubbed.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${
      window.location.hash
    }`
  );
}
const isPopup =
  typeof config.popup === 'string'
    ? config.popup === 'true'
    : Boolean(config.popup);

// Defer mounting the SUSI Light web component until the user clicks a
// "Sign in" button. Lets us measure the warmed-cache scenario: the wrapper's
// hidden preload iframe primes the network in the background while the user
// looks at the button, so mounting <susi-sentry-light> later should be near
// instant.
//
// Resolution order:
//   1. `?useDialog=` query param wins if present (`true`/`false`).
//   2. Else default to `true` only for the react renderer (where preload
//      actually runs). All other renderers mount the component immediately
//      so the demo page stays demo-like.
const useDialogParam = searchParams.get('useDialog');
const useDialog =
  useDialogParam !== null
    ? useDialogParam !== 'false'
    : config.renderer === 'react';

@customElement('sample-app')
export class SampleApp extends LitElement {
  static styles = css`
    * {
      box-sizing: border-box;
    }

    :host section {
      display: grid;
      grid-template-rows: 65px auto;
      background: var(--spectrum-gray-75) !important;
    }

    :host main {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      overflow-y: auto;
    }

    :host,
    section {
      width: 100%;
      height: 100%;
    }

    progress {
      visibility: hidden;
      width: 334px;
      margin-bottom: 20px;
    }

    progress.show {
      visibility: visible;
    }

    .language-picker {
      background-color: var(--spectrum-gray-75);
      border: solid 1px var(--spectrum-gray-200);
      color: var(--spectrum-gray-900);
      margin-left: 10px;
      padding: 5px;
    }

    .theme-button {
      background-color: var(--spectrum-gray-75);
      border: solid 1px var(--spectrum-gray-200);
      color: var(--spectrum-gray-900);
      margin-left: 10px;
      padding: 5px;
    }

    .card {
      display: flex;
      justify-content: center;
      align-items: center;

      background: var(--spectrum-gray-75, #fdfdfd);
      border-radius: 4px;
      border: solid 1px var(--spectrum-gray-200, #e6e6e6);
      /* Clip the SUSI Light iframe (incl. the square DCP panel) to this
         card's rounded corners — the host page owns the outer border/radius. */
      overflow: hidden;
    }

    .card-full-width {
      width: 500px;
    }

    .card-express-theme {
      width: 360px;
      height: 500px;
      padding: 32px;
    }

    .card-large-buttons {
      width: 334px;
      height: 482px;
      padding: 26px;
    }

    .card-complete-account {
      width: 510px;
      height: 700px;
      padding: 56px;
    }

    .card iframe {
      width: 100%;
      height: 100%;
    }

    .code {
      width: 600px;
      font-size: 18px;
      word-break: break-word;
    }

    .adobe-logo {
      width: 85px;
      display: flex;
      justify-content: space-between;
    }

    .adobe-logo__text {
      font-weight: 700;
      font-size: 18px;
      color: #fa0f00;
    }

    .global-nav {
      display: flex;
      justify-content: center;
      background: var(--spectrum-gray-75);
      border-bottom: solid 1px var(--spectrum-gray-200);
      color: var(--spectrum-gray-900);
    }

    .global-nav__navigation {
      max-width: 1440px;
      flex-grow: 1;
    }

    .global-nav__navigation-list {
      display: flex;
    }

    .global-nav,
    .global-nav__navigation-list,
    .global-nav__navigation-list-item {
      height: 100%;
    }

    .global-nav__navigation-list,
    .global-nav__navigation-list-item {
      padding: 0;
      margin: 0;
    }

    .global-nav__navigation-list-item {
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-family: adobe-clean, Source Sans Pro, -apple-system,
        BlinkMacSystemFont, Segoe UI, Roboto, sans-serif;
      font-size: 14px;
      font-weight: 400;
      margin: 0 16px;
    }

    .global-nav__navigation-list-item--right {
      margin-left: auto;
    }

    sp-switch {
      color: var(--spectrum-gray-900);
      font-family: adobe-clean, Source Sans Pro, -apple-system,
        BlinkMacSystemFont, Segoe UI, Roboto, sans-serif;
      font-size: 14px;
      padding: 10px;
    }

    .options-container {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 24px;
    }

    .sign-in-cta {
      font-family: adobe-clean, Source Sans Pro, -apple-system,
        BlinkMacSystemFont, Segoe UI, Roboto, sans-serif;
      font-size: 16px;
      font-weight: 600;
      padding: 12px 32px;
      border-radius: 20px;
      border: 0;
      background: #1473e6;
      color: #fff;
      cursor: pointer;
    }

    .sign-in-cta:hover {
      background: #0d66d0;
    }

    .sign-in-cta:focus-visible {
      outline: 2px solid #1473e6;
      outline-offset: 2px;
    }

    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }

    .modal-card {
      position: relative;
      padding: 24px;
      border-radius: 16px;
      max-height: 95vh;
      overflow: auto;
      /* .card sets align-items:center, which vertically centers the iframe once it's taller
         than this box's capped height — the overflowing top half then sits above the scrollable
         viewport, and centered start-side overflow isn't reachable by scrolling in most browsers.
         Aligning to the start keeps the iframe's true top at this box's top, so the whole height
         is reachable via the scrollbar. */
      align-items: flex-start;
      /* Thin, inset, rounded-thumb scrollbar — the default native scrollbar is a wide, flush,
         square-edged bar that visually clashes with this card's rounded corners. Firefox/modern
         Chromium read the standard properties; the ::-webkit- rules below cover Safari/Chrome. */
      scrollbar-width: thin;
      scrollbar-color: rgba(0, 0, 0, 0.25) transparent;
    }

    .modal-card::-webkit-scrollbar {
      width: 10px;
    }

    .modal-card::-webkit-scrollbar-track {
      background: transparent;
    }

    .modal-card::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.25);
      border-radius: 8px;
      /* Transparent border + content-box clip insets the visible thumb within the track, so it
         reads as a slim floating pill instead of a bar flush against the card's rounded edge. */
      border: 3px solid transparent;
      background-clip: content-box;
    }

    .modal-card::-webkit-scrollbar-thumb:hover {
      background-color: rgba(0, 0, 0, 0.4);
    }

    .modal-close {
      position: absolute;
      top: 16px;
      right: 16px;
      width: 28px;
      height: 28px;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: transparent;
      color: #2c2c2c;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.15s ease;
      z-index: 1;
    }

    .modal-close:hover {
      background: #ebebeb;
    }

    .modal-close:focus-visible {
      outline: 2px solid #1473e6;
      outline-offset: 2px;
    }

    .modal-close svg {
      width: 14px;
      height: 14px;
      display: block;
    }

    .redirect-link {
      font-family: adobe-clean, Source Sans Pro, -apple-system;
      text-decoration: none;
    }

    section {
      height: 100%;
    }

    sp-theme {
      height: 100%;
    }
  `;

  @property()
  isPopup = isPopup;

  @property({ type: Boolean })
  resumeSocial = resumeSocial;

  // When `useDialog` is on (the default), we don't mount <susi-sentry-light>
  // until the user explicitly asks for it by clicking the Sign in button.
  // This flag flips once on first click and stays true for the rest of the
  // session.
  //
  // Auto-mount when `resumeSocial` is true so the user lands directly in
  // the in-progress UI after returning from redirect-mode social sign-up —
  // forcing them to click "Sign in" again would be confusing (the OAuth
  // step is already done).
  @property({ type: Boolean })
  signInRequested = !useDialog || resumeSocial;

  @property({ type: Object })
  userData: { displayName: string; email: string } | null = null;

  @property()
  token = '';

  @property({ type: Object })
  authState = {
    client_id: 'sentry-test',
    scope: 'AdobeID,openid',
    locale: 'en-us',
    response_type: 'token',
    ...authParams.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
    dt: this.isDarkMode,
  };

  @property({ type: Object })
  config = {
    ...config,
    consentProfile: config.consentProfile ?? 'adobe-id-sign-up',
    enableCompleteAccount: config.enableCompleteAccount ?? false,
    enableProgressiveAccount: config.enableProgressiveAccount ?? false,
  };

  private _onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.signInRequested && useDialog) {
      this.dismissSignIn();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    window.SENTRY_EVENTS = [];
    document.addEventListener('keydown', this._onEscape);
    const qs = new URLSearchParams(window.location.hash.substring(1));
    if (qs.has('access_token')) {
      this._onToken({ detail: qs.get('access_token') } as CustomEvent<string>);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onEscape);
    super.disconnectedCallback();
  }

  get isDarkMode() {
    const [_, dtValue] = authParams.find(([key]) => key === 'dt') || [];

    return dtValue
      ? dtValue === 'true'
      : window?.matchMedia('(prefers-color-scheme: dark)')?.matches;
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
  ): void {
    [
      'on-load',
      'on-auth-failed',
      'on-analytics',
      'on-modal-open',
      'on-modal-close',
    ].forEach(ev => {
      const sentryElement = this.shadowRoot?.querySelector('#sentry');
      if (sentryElement) {
        sentryElement.addEventListener(ev, e => {
          window.SENTRY_EVENTS.push({
            name: e.type,
            detail: e.detail,
          });
        });
      }
    });
  }

  async _onRedirect(e: any) {
    const redirectUri = e.detail;
    await preRedirectWork.apply(this);
    window.location.assign(redirectUri);
  }

  _onProviderClicked(e: CustomEvent) {
    console.log('provider clicked', e.detail);
  }

  async _onToken(e: CustomEvent<string>) {
    console.log('on token', e.detail);
    // The redirect-mode social sign-up flow has handed off a token, so the
    // resume is complete. Clear the flag before any state change below
    // re-renders <susi-sentry-light> — otherwise the wrapper would re-mount the
    // iframe with resumeSocial still true and re-run the one-shot federated flow.
    this.resumeSocial = false;
    const token = e.detail;
    if (searchParams.get('disable_local_msw') === 'true') {
      this.token = token;
    }

    await window.adobeIMS.refreshToken();
    this.userData = await window.adobeIMS.getProfile();
  }

  async _onAuthCode(e: CustomEvent) {
    const code = e.detail;
    if (searchParams.get('disable_local_msw') === 'true') {
      this.token = code;
    }
  }

  async _onAuthFailed(e: CustomEvent) {
    // Every on-auth-failed reason is a terminal escape (see contextual-signin.md's event
    // table) — the host always navigates to fallbackUrl when one is present, regardless of
    // which reason produced it. Used to allowlist specific reasons (popup-blocked, ride-error),
    // which silently no-op'd for every new reason added since (existingFederatedOrEnterpriseAccount,
    // and all of postAuthGates'/completeAuthentication's reasons — profileChooserNeeded,
    // mandatoryProfilePending, emailVerificationRequired, termsOfUsePending, optionalProfilePending,
    // transient5xx, handover's own reasons) even though those already send a real fallbackUrl.
    if (e.detail.fallbackUrl) {
      await preRedirectWork.apply(this);
      window.location.assign(e.detail.fallbackUrl);
    }
  }

  async _onAnalytics(e: CustomEvent) {
    console.log(e);
  }

  async _onError(e: CustomEvent) {
    if (e.detail.name === 'critical') {
      console.error('critical', e);
    }

    if (e.detail.name === 'unrecoverable') {
      console.error('unrecoverable', e);
    }
  }

  onThemeChange() {
    this.authState = {
      ...this.authState,
      dt: !this.authState.dt,
    };
  }

  onSignInClick = () => {
    this.signInRequested = true;
  };

  dismissSignIn = () => {
    this.signInRequested = false;
  };

  private _stopClick = (e: Event) => {
    e.stopPropagation();
  };

  get variant() {
    if (config.variant) {
      return config.variant;
    }

    return 'standard';
  }

  get authParams() {
    const extraParams: Record<string, string> = {};
    const serviceParams: Record<string, string> = {};

    // Redirect back to whatever origin serves the sample — the sample runs on
    // local/stage/prestage/preprod/prod and should return to its own host. Was
    // hardcoded to the stage host, so this was only ever correct on stage.
    extraParams['redirect_uri'] = window.location.origin;

    if (searchParams.get('disable_local_msw') === 'true') {
      serviceParams['disable_local_msw'] = 'true';
    }

    return { ...this.authState, ...extraParams, ...serviceParams };
  }

  localeChanged = (e: any) => {
    this.authState = {
      ...this.authParams,
      locale: e.target.value,
    };
  };

  get autofocus() {
    return config?.autofocus?.toString();
  }

  @property()
  dialogOpen = false;

  @property()
  dialogDimensions: { width: number; height: number } = { width: 0, height: 0 };

  openDialog(dimensions: { width: number; height: number }) {
    const dialog = this.shadowRoot?.getElementById('dialog') as DialogElement;
    if (dialog && 'openDialog' in dialog) {
      dialog.openDialog();
    }
    this.dialogOpen = true;
    this.dialogDimensions = dimensions;
  }

  closeDialog() {
    const dialog = this.shadowRoot?.getElementById('dialog') as DialogElement;
    if (dialog) {
      dialog.closeDialog();
    }
    this.dialogOpen = false;
  }

  susiLight = () => {
    return html`<susi-sentry
      id="sentry"
      .authParams=${this.authParams}
      .config=${this.config}
      .popup=${this.isPopup}
      .variant=${this.variant}
      @on-auth-code=${this._onAuthCode}
      @on-auth-failed=${this._onAuthFailed}
      @on-error=${this._onError}
      @on-load=${(e: CustomEvent) => console.log('onload', e.detail.ready)}
      @on-provider-clicked=${this._onProviderClicked}
      @on-modal-open=${e => {
        console.log('on complete account modal open', e.detail);
        this.openDialog(e.detail.dimensions);
      }}
      @on-modal-close=${e => {
        console.log('on complete account modal close', e.detail);
        this.closeDialog();
      }}
      @on-token=${this._onToken}
      @redirect=${this._onRedirect}
    ></susi-sentry>`;
  };

  susiLightElm = () => {
    return html`<susi-sentry-light
      id="sentry"
      .authParams=${this.authParams}
      .config=${this.config}
      .popup=${this.isPopup}
      .variant=${this.variant}
      .autofocus=${this.autofocus}
      .resumeSocial=${this.resumeSocial}
      @on-analytics=${this._onAnalytics}
      @on-auth-code=${this._onAuthCode}
      @on-auth-failed=${this._onAuthFailed}
      @on-error=${this._onError}
      @on-load=${(e: CustomEvent) => console.log('onload', e.detail.ready)}
      @on-provider-clicked=${this._onProviderClicked}
      @on-token=${this._onToken}
      @redirect=${this._onRedirect}
    ></susi-sentry-light>`;
  };

  render() {
    return html`
      <sp-theme theme="spectrum" color=${this.authState.dt ? 'dark' : 'light'}>
        <section>
          <header class="global-nav">
            <nav class="global-nav__navigation">
              <ul class="global-nav__navigation-list">
                <li class="global-nav__navigation-list-item">
                  <div class="adobe-logo">
                    <img
                      class="adobe-logo__icon"
                      src="https://www.adobe.com/content/dam/cc/icons/Adobe_Corporate_Horizontal_Red_HEX.svg"
                      width="25px"
                    />
                    <span class="adobe-logo__text">Adobe</span>
                  </div>
                </li>
                <li class="global-nav__navigation-list-item">
                  <a
                    href="https://git.corp.adobe.com/pages/adobe-identity/identity-sentry/"
                    class="redirect-link"
                    >Read Docs</a
                  >
                </li>
                <li class="global-nav__navigation-list-item">
                  <a
                    href="https://auth-light-tessera.identity-stage.adobe.com/"
                    class="redirect-link"
                    >React sample</a
                  >
                </li>
                <li
                  class="global-nav__navigation-list-item global-nav__navigation-list-item--right"
                >
                  ${this.userData
                    ? html`<span
                        >Signed in as ${this.userData?.displayName}</span
                      >`
                    : html`<span>Sign in</span>`}
                  ${this.userData?.email
                    ? html`<span>(${this.userData?.email})</span>`
                    : nothing}
                </li>
              </ul>
            </nav>
          </header>
          <main>
            ${this.token
              ? html`<section id="token">${this.token}</section>`
              : nothing}
            <div class="options-container">
              <select class="language-picker" @change=${this.localeChanged}>
                ${locales.map(
                  locale => html`
                    <option
                      value=${locale}
                      ?selected=${locale === this.authParams.locale}
                    >
                      ${locale}
                    </option>
                  `
                )}
              </select>
              <button
                class="theme-button"
                type="button"
                @click=${this.onThemeChange}
              >
                Dark Mode: ${this.authState.dt ? 'ON ' : 'OFF'}
              </button>
            </div>
            ${this.signInRequested
              ? useDialog
                ? html`
                    <div
                      class="modal-backdrop"
                      role="presentation"
                      @click=${this.dismissSignIn}
                    >
                      <div
                        class=${`modal-card card card-${this.variant} ${
                          config.fullWidth ? 'card-full-width' : ''
                        }`}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Sign in"
                        @click=${this._stopClick}
                      >
                        <button
                          class="modal-close"
                          type="button"
                          aria-label="Close"
                          @click=${this.dismissSignIn}
                        >
                          <svg
                            viewBox="0 0 16 16"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M3 3L13 13M13 3L3 13"
                              stroke="currentColor"
                              stroke-width="1.6"
                              stroke-linecap="round"
                            />
                          </svg>
                        </button>
                        ${[
                          'edu-express',
                          'react',
                          'sandbox',
                          'standard',
                        ].includes(this.variant)
                          ? this.susiLightElm()
                          : this.susiLight()}
                      </div>
                    </div>
                  `
                : html`
                    <div
                      class=${`card card-${this.variant} ${
                        config.fullWidth ? 'card-full-width' : ''
                      }`}
                    >
                      ${[
                        'edu-express',
                        'react',
                        'sandbox',
                        'standard',
                      ].includes(this.variant)
                        ? this.susiLightElm()
                        : this.susiLight()}
                    </div>
                  `
              : html`
                  <button
                    class="sign-in-cta"
                    type="button"
                    @click=${this.onSignInClick}
                  >
                    Sign in
                  </button>
                `}

            <lit-dialog
              width=${`${this.dialogDimensions.width}px`}
              height=${`${this.dialogDimensions.height}px`}
              id="dialog"
            >
            </lit-dialog>
          </main>
        </section>
      </sp-theme>
    `;
  }
}
